import * as Rollbar from "rollbar";
import type { App } from "vue";

const accessToken = process.env.VUE_APP_ROLLBAR_ACCESS_TOKEN;
const rawEnvironment =
  process.env.VUE_APP_ROLLBAR_ENV || process.env.NODE_ENV || "production";
// Bitbucket passes the deployment environment name (e.g. "Staging", "Test"),
// so normalize case and treat "staging" as the "test" environment.
const normalizedEnvironment = rawEnvironment.toLowerCase();
const environment =
  normalizedEnvironment === "staging" ? "test" : normalizedEnvironment;
type RollbarInstance = {
  error: (error: Error | string, context?: Record<string, unknown>) => void;
  info: (message: Error | string, context?: Record<string, unknown>) => void;
};

type RollbarConstructor = new (
  config: Record<string, unknown>,
) => RollbarInstance;

function resolveRollbarConstructor(): RollbarConstructor {
  const mod = Rollbar as unknown as RollbarConstructor & {
    default?: RollbarConstructor;
  };
  return mod.default ?? mod;
}

const RollbarClient = resolveRollbarConstructor();

const rollbar =
  accessToken && ["production", "development", "test"].includes(environment)
    ? new RollbarClient({
        accessToken,
        captureUncaught: true,
        captureUnhandledRejections: true,
        // Report only first-party errors (bundle URLs on our own domains),
        // filtering out browser-extension / third-party script noise. Matched
        // as regex against each stack-frame filename; eval'd booking-condition
        // code keeps its app-domain origin in the frame, so it's captured too.
        hostSafeList: [
          "covomo-services\\.com",
          "covomo\\.de",
          "covomo\\.com",
          "versichat\\.de",
        ],
        autoInstrument: {
          log: true,
          network: false,
        },
        payload: {
          environment,
          client: {
            javascript: {
              code_version: process.env.VUE_APP_VERSION || "unknown",
            },
          },
        },
      })
    : null;

export default {
  install(app: App) {
    app.provide("rollbar", rollbar);

    if (!rollbar) return;

    app.config.errorHandler = (error, instance, info) => {
      rollbar.error(error as Error, {
        info,
        component: instance?.$options?.name || "unknown",
      });

      console.error(error);
    };

    app.config.globalProperties.$rollbar = rollbar;
  },
};

export { rollbar };
